import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import FunnelLayout from "./FunnelLayout";
import "./CommonNewFunnel.css";

const LS_KEY_STEP1 = "glp1_intake_step1";
const LS_KEY_STEP3 = "glp1_intake_step3";
const LS_KEY_STEP10 = "glp1_intake_step10";

const stateOptions: Record<string, string> = {
  AK: "Alaska",
  AL: "Alabama",
  AR: "Arkansas",
  AZ: "Arizona",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DC: "District of Columbia",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  IA: "Iowa",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  MA: "Massachusetts",
  MD: "Maryland",
  ME: "Maine",
  MI: "Michigan",
  MN: "Minnesota",
  MO: "Missouri",
  MS: "Mississippi",
  MT: "Montana",
  NC: "North Carolina",
  ND: "North Dakota",
  NE: "Nebraska",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NV: "Nevada",
  NY: "New York",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VA: "Virginia",
  VT: "Vermont",
  WA: "Washington",
  WI: "Wisconsin",
  WV: "West Virginia",
  WY: "Wyoming",
};

type Step1State = {
  feet?: string;
  inches?: string;
  weight?: string;
  goalWeight?: string;
};

type PaceChoice = "" | "works" | "faster" | "too_fast";

type Step3Saved = {
  pace?: PaceChoice;
  weeksWorks?: number;
  weeksFaster?: number;
};

type Step10State = {
  firstName: string;
  lastName: string;

  stateCode: string; // "CA"
  stateName: string; // "California"

  // backward compat
  state?: string; // old
};

const DEFAULT: Step10State = {
  firstName: "",
  lastName: "",
  stateCode: "",
  stateName: "",
};

const ChevronIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <path
      d="M4.51581 7.54794C4.95181 7.10194 5.55881 7.06694 6.09181 7.54794L9.99981 11.2949L13.9078 7.54794C14.4408 7.06694 15.0488 7.10194 15.4818 7.54794C15.9178 7.99294 15.8898 8.74494 15.4818 9.16294C15.0758 9.58094 10.7868 13.6649 10.7868 13.6649C10.5698 13.8879 10.2848 13.9999 9.99981 13.9999C9.71481 13.9999 9.42981 13.8879 9.21081 13.6649C9.21081 13.6649 4.92381 9.58094 4.51581 9.16294C4.10781 8.74494 4.07981 7.99294 4.51581 7.54794Z"
      fill="#38312C"
      fillOpacity="0.2"
    />
  </svg>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="funnel-field">
    <div className="funnel-field__label">{label}</div>
    {children}
  </div>
);

const toNumber = (s?: string) => {
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const calcHeightInches = (feet?: string, inches?: string) => {
  const f = toNumber(feet);
  const i = toNumber(inches);
  return f * 12 + i;
};

const calcBMI = (weightLbs: number, heightIn: number) => {
  if (!weightLbs || !heightIn) return 0;
  return (weightLbs / (heightIn * heightIn)) * 703;
};

const NewFunnelStep10: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<Step10State>(DEFAULT);
  const [openSelect, setOpenSelect] = useState<null | "state">(null);

  // ✅ KEY FIX: stop overwriting localStorage before loading
  const [hydrated, setHydrated] = useState(false);

  // ✅ Load + normalize (supports older saved shape)
  useEffect(() => {
    const saved = localStorage.getItem(LS_KEY_STEP10);

    if (saved) {
      try {
        const parsed = JSON.parse(saved) as Partial<Step10State>;
        const savedCode = parsed.stateCode || parsed.state || "";
        const savedName = parsed.stateName || (savedCode ? stateOptions[savedCode] : "") || "";

        setForm({
          ...DEFAULT,
          ...parsed,
          stateCode: savedCode,
          stateName: savedName,
          state: savedCode, // keep old key in sync
        });
      } catch {
        setForm(DEFAULT);
      }
    } else {
      setForm(DEFAULT);
    }

    setHydrated(true);
  }, []);

  // ✅ Persist ONLY after hydration
  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(LS_KEY_STEP10, JSON.stringify(form));
  }, [form, hydrated]);

  // Step 1 (BMI / weights)
  const step1 = useMemo<Step1State>(() => {
    const saved = localStorage.getItem(LS_KEY_STEP1);
    if (!saved) return {};
    try {
      return JSON.parse(saved);
    } catch {
      return {};
    }
  }, []);

  const heightIn = calcHeightInches(step1.feet, step1.inches);
  const currentWeight = toNumber(step1.weight);
  const goalWeight = toNumber(step1.goalWeight);
  const bmi = calcBMI(currentWeight, heightIn);

  // Weeks from Step 3
  const weeksToGoal = useMemo(() => {
    const s3 = localStorage.getItem(LS_KEY_STEP3);
    if (!s3) return 0;

    try {
      const p = JSON.parse(s3) as Step3Saved;
      const pace = (p.pace || "") as PaceChoice;

      if (pace === "faster") return Number(p.weeksFaster || 0);
      if (pace === "works") return Number(p.weeksWorks || 0);

      return Number(p.weeksWorks || 0); // fallback for too_fast
    } catch {
      return 0;
    }
  }, []);

  const weeksText = weeksToGoal > 0 ? weeksToGoal.toFixed(2) : "--";

  const canGoNext =
    form.firstName.trim().length > 0 &&
    form.lastName.trim().length > 0 &&
    form.stateCode.trim().length > 0 &&
    form.stateName.trim().length > 0;

  const next = () => {
    if (!canGoNext) return;
    navigate("/glp1/intake-form/step-10");
  };

  return (
    <FunnelLayout>
      <h1 className="title-step-1 funnel-q-title-heading" style={{ color: "#96c6da", textAlign: "center" }}>
        Your Medical Review
      </h1>

      <div className="funnel-section">
        <div className="review-lines">
          <div className="review-line">
            <span style={{ fontWeight: "bold" }}>BMI: </span>
            <span>{bmi ? bmi.toFixed(2) : "--"}</span>
          </div>

          <div className="review-line">
            <span style={{ fontWeight: "bold" }}>Current Weight: </span>
            <span>{currentWeight ? `${currentWeight}lbs` : "--"}</span>
          </div>

          <div className="review-line">
            <span style={{ fontWeight: "bold" }}>Goal Weight: </span>
            <span>
              {goalWeight ? (
                <>
                  {goalWeight}lbs{" "}
                  <span style={{ textDecoration: "underline" }}>within <span style={{ letterSpacing: '1px'}}>{weeksText}</span> weeks</span>
                </>
              ) : (
                "--"
              )}
            </span>
          </div>
        </div>

        <div className="funnel-divider" />

        <p className="subtitle-step-1" style={{ marginBottom: "-14px" }}>
          You are a <strong>strong candidate</strong> for medical weight loss with a{" "}
          <strong>94% chance</strong> of successful treatment if qualified.
        </p>
      </div>

      <div className="funnel-divider" />

      <h2 className="title-step-1" style={{ fontSize: "28px" }}>
        Let&apos;s proceed to check your eligibility.
      </h2>

      <div className="funnel-section" style={{ marginTop: "40px" }}>
        <Field label="First Name">
          <input
            className="funnel-input"
            value={form.firstName}
            onChange={(e) => setForm((p) => ({ ...p, firstName: e.target.value }))}
            placeholder="First Name"
          />
        </Field>

        <Field label="Last Name">
          <input
            className="funnel-input"
            value={form.lastName}
            onChange={(e) => setForm((p) => ({ ...p, lastName: e.target.value }))}
            placeholder="Last Name"
          />
        </Field>

        <Field label="What state will your medication be shipped to?">
          <div className={`funnel-select ${openSelect === "state" ? "is-open" : ""}`}>
            <select
              className="funnel-input"
              value={form.stateCode}
              onFocus={() => setOpenSelect("state")}
              onBlur={() => setOpenSelect(null)}
              onMouseDown={() => setOpenSelect("state")}
              onKeyDown={() => setOpenSelect("state")}
              onChange={(e) => {
                const code = e.target.value;
                const name = stateOptions[code] || "";
                setForm((p) => ({
                  ...p,
                  stateCode: code,
                  stateName: name,
                  state: code, // keep old key updated too
                }));
                setOpenSelect(null);
              }}
            >
              <option value="" disabled>
                Select a state
              </option>

              {/* UI shows only full name */}
              {Object.entries(stateOptions).map(([abbr, name]) => (
                <option key={abbr} value={abbr}>
                  {name}
                </option>
              ))}
            </select>

            <span className="funnel-select__icon" aria-hidden="true">
              <ChevronIcon />
            </span>
          </div>

          <p className="subtitle-step-1" style={{ marginTop: "10px" }}>
            Your information is never shared and is protected by HIPAA.
          </p>
        </Field>
      </div>

      <button className="next-btn" onClick={next} disabled={!canGoNext}>
        Next →
      </button>
    </FunnelLayout>
  );
};

export default NewFunnelStep10;